
import { useInventoryState } from "./useInventoryState";
import { useAuthStore } from "../../../store/authStore";
import { useInventoryData } from "./useInventoryData";
import { useInventoryFilters } from "./useInventoryFilters";
import { useInventoryForm } from "./useInventoryForm";
import { useInventoryActions } from "./useInventoryActions";
import { useInventoryStats } from "../../../hooks/useGlobalStats";
import { INVENTORY_SUB_TABS } from "../../../constants/globalConstants";

export const useInventoryLogic = () => {
  const state = useInventoryState();
  const { user } = useAuthStore();
  const dataHandlers = useInventoryData(user, state.activeTab, state);
  const filterLogic = useInventoryFilters(state, user);
  const formHandlers = useInventoryForm(state.activeTab, state.masterItems, filterLogic.activeInventory, { 
    suppliers: state.suppliers, 
    stores: state.stores 
  });
  const actions = useInventoryActions(state, dataHandlers, formHandlers);

  const stats = useInventoryStats({
    inventory: filterLogic.allAggregatedItems,
    sales: state.sales,
    returns: state.materialReturns,
    requests: state.materialRequests,
    handlePurchaseReturnPayment: actions.handlePurchaseReturnPayment,
    handleSaleReturnPayment: actions.handleSaleReturnPayment,
    purchaseReturns: state.purchaseReturns,
    openingStocks: state.openingStocks,
    activeTab: state.activeTab
  });

  const getInventorySubTabs = () => [
    // { id: INVENTORY_SUB_TABS.ITEMS, label: "Inventory Master" },
    { id: INVENTORY_SUB_TABS.PURCHASES, label: "Purchase" },
    { id: INVENTORY_SUB_TABS.SALES, label: "Sales" },
    { id: INVENTORY_SUB_TABS.OPEN_STOCK, label: "Closing Stock" }
  ];

  const handleEdit = (item) => {
    state.setEditingItem(item);
    formHandlers.setForm({ ...formHandlers.form, ...item });
    if (item.invoiceNo === 'OPEN-STOCK' || item.supplier === 'Opening Stock') {
       state.setOpenStockOpen(true);
    } else {
       state.setPurchaseOpen(true);
    }
  };

  const handleNewPurchase = () => {
    state.setEditingItem(null);
    formHandlers.resetForm();
    state.setPurchaseOpen(true);
  };

  const handleNewOpenStock = () => {
    state.setEditingItem(null);
    formHandlers.resetForm({
      invoiceNo: "OPEN-STOCK",
      invoiceDate: new Date().toISOString().split('T')[0]
    });
    state.setOpenStockOpen(true);
  };
  
  const handleEditOpenStock = (item) => {
    // Find the raw record from openingStocks state (has full populated objects for store/category)
    // instead of using the filteredOpenStock item (which has name strings already)
    const rawRecord = (state.openingStocks || []).find(os => os._id === (item._id || item)) || item;
    state.setEditingItem(rawRecord);
    state.setEditingOpenStock(rawRecord);
    formHandlers.setForm({ ...formHandlers.form, ...rawRecord });
    state.setOpenStockOpen(true);
  };

  return {
    ...state,
    projects: state.projectsLocal,
    purchases: state.purchases,
    user,
    ...filterLogic,
    ...formHandlers,
    ...actions,
    refreshAll: dataHandlers.refreshAll,
    
    itemCount: filterLogic.allAggregatedItems.length,
    totalUnits: filterLogic.activeInventory.reduce((s, i) => s + (i.availableQty || 0), 0),
    totalValue: stats.stock.totalValue,
    noStockCount: filterLogic.noStock.length,
    averageValuePerItem: filterLogic.allAggregatedItems.length ? stats.stock.totalValue / filterLogic.allAggregatedItems.length : 0,
    
    stats: {
      stock: {
        ...stats.stock,
        totalItems: filterLogic.allAggregatedItems.length,
        totalUnits: filterLogic.activeInventory.reduce((s, i) => s + (i.availableQty || 0), 0),
        distribution: stats.stock.distribution || []
      },
      sales: stats.sales,
      requests: stats.requests,
      returns: stats.returns
    },
    
    requestStats: stats.requests,
    activeReturnStats: stats.returns,
    activeReturnFilterOptions: { 
      categories: state.cats || [], 
      parties: (() => {
        const pMap = new Map();
        (state.suppliers || []).forEach(s => {
          const name = (s.vendorName || s.name || s.title || "").trim();
          if (name && !pMap.has(name.toLowerCase())) {
            pMap.set(name.toLowerCase(), { ...s, name, type: 'vendor' });
          }
        });
        (state.projectsLocal || []).forEach(p => {
          const name = (p.customerName || p.name || "").trim();
          if (name && !pMap.has(name.toLowerCase())) {
             pMap.set(name.toLowerCase(), { _id: p._id, name, type: 'customer' });
          }
        });
        return Array.from(pMap.values());
      })()
    },
    purchaseStats: {
      totalAmount: (state.purchases || []).reduce((sum, p) => sum + (Number(p.netPayable) || 0), 0),
      totalItems: (state.purchases || []).reduce((sum, p) => sum + (p.items || []).reduce((isum, it) => isum + (Number(it.qty) || 0), 0), 0),
      transactions: (state.purchases || []).length,
      suppliersCount: new Set((state.purchases || []).map(p => p.vendor?._id || p.vendor || '').filter(Boolean)).size,
      distribution: []
    },
    saleStats: stats.sales,
    openStockStats: stats.openStock,
    purchasedMap: filterLogic.purchasedMap,

    handleEdit,
    handleNewPurchase,
    handleNewOpenStock,
    handleEditOpenStock,
    getInventorySubTabs,
    
    handleViewPurchases: (item) => {
      state.setSearch(item.name || item.itemName || "");
      state.setFilters({
        category: "all",
        supplier: "all",
        status: "all",
        date: "all",
        fromDate: "",
        toDate: ""
      });

      const hasActualPurchase = (state.purchases || []).some(p => p.items?.some(i => String(i.item?._id || i.item) === String(item._id)));
      const hasActualOpenStock = (state.openStocks || []).some(os => String(os.item?._id || os.item) === String(item._id) || (os.itemCode && os.itemCode === item.itemCode) || (os.name && os.name === (item.itemName || item.name)));

      if (hasActualOpenStock && !hasActualPurchase) {
        state.setInventorySubTab(INVENTORY_SUB_TABS.OPEN_STOCK);
      } else if (hasActualPurchase) {
        state.setInventorySubTab(INVENTORY_SUB_TABS.PURCHASES);
      } else if (item.hasOpenStock) {
        state.setInventorySubTab(INVENTORY_SUB_TABS.OPEN_STOCK);
      } else {
        state.setInventorySubTab(INVENTORY_SUB_TABS.PURCHASES);
      }
    },

    finalRestockReturn: actions.finalRestockReturn,
    
    dynamicSettings: { 
      cats: (() => {
        const cMap = new Map();
        (state.cats || []).forEach(c => {
          const name = String(c.categoryName || c.title || c.name || "").trim();
          if (name && !cMap.has(name.toLowerCase())) cMap.set(name.toLowerCase(), c);
        });
        // Pull unique categories from inventory items
        (state.inventory || []).forEach(i => {
          const val = typeof i.category === 'object' ? (i.category?.title || i.category?.name) : i.category;
          const name = String(val || "").trim();
          if (name && !cMap.has(name.toLowerCase())) cMap.set(name.toLowerCase(), { _id: name, categoryName: name });
        });
        // Pull unique categories from purchases
        (state.purchases || []).forEach(p => {
          const val = typeof p.category === 'object' ? (p.category?.title || p.category?.name) : (p.category || p.item?.category?.title || p.item?.category?.name || p.item?.category);
          const name = String(val || "").trim();
          if (name && !cMap.has(name.toLowerCase())) cMap.set(name.toLowerCase(), { _id: name, categoryName: name });
        });
        return Array.from(cMap.values());
      })(), 
      units: state.units, 
      suppliers: (() => {
        const sMap = new Map();
        (state.suppliers || []).forEach(s => {
          const name = String(s.vendorName || s.name || s.title || "").trim();
          if (name && !sMap.has(name.toLowerCase())) {
            sMap.set(name.toLowerCase(), s);
          }
        });
        // Pull unique vendors from purchases
        (state.purchases || []).forEach(p => {
          const val = p.vendorName || (typeof p.vendor === 'object' && p.vendor ? (p.vendor.vendorName || p.vendor.name || p.vendor._id) : p.vendor) || (typeof p.supplier === 'object' && p.supplier ? (p.supplier.name || p.supplier._id) : p.supplier);
          const name = String(val || "").trim();
          if (name && !sMap.has(name.toLowerCase())) {
            sMap.set(name.toLowerCase(), { _id: name, vendorName: name });
          }
        });
        return Array.from(sMap.values());
      })(), 
      brands: state.brands, 
      selectedPRForPayment: actions.selectedPRForPayment, setSelectedPRForPayment: actions.setSelectedPRForPayment,
      selectedSRForPayment: actions.selectedSRForPayment, setSelectedSRForPayment: actions.setSelectedSRForPayment, 
      stores: state.stores, 
      masterItems: state.masterItems,
      subCats: (() => {
        const scMap = new Map();
        (state.subCats || []).forEach(sc => {
          const name = String(sc.subCategoryName || sc.title || sc.name || "").trim();
          if (name && !scMap.has(name.toLowerCase())) scMap.set(name.toLowerCase(), sc);
        });
        // Pull unique subCategories from purchases
        (state.purchases || []).forEach(p => {
          const scVal = typeof p.subCategory === 'object'
            ? (p.subCategory?.subCategoryName || p.subCategory?.name || p.subCategory?.title)
            : (p.subCategory || p.item?.subCategory?.name || p.item?.subCategory);
          const scName = String(scVal || "").trim();
          if (!scName) return;
          const catVal = typeof p.category === 'object'
            ? (p.category?.categoryName || p.category?.title || p.category?.name)
            : (p.category || p.item?.category?.categoryName || p.item?.category?.name || p.item?.category);
          const catName = String(catVal || "").trim();
          if (!scMap.has(scName.toLowerCase())) {
            scMap.set(scName.toLowerCase(), { _id: scName, subCategoryName: scName, category: catName });
          }
        });
        return Array.from(scMap.values());
      })()
    }
  };
};

